import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
  Alert,
  Switch,
  ListSubheader,
} from "@mui/material";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
} from "@react-google-maps/api";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAdvertisementMetaApi,
  getAdvertisementByIdApi,
  addAdvertisementApi,
  updateAdvertisementApi,
  publishAdvertisementApi,
  testAdvertisementTargetingApi,
  getAllStateApi,
  getCityByStateIdApi,
  getAppSettingsApi,
  getAllEventApi,
} from "../../../redux/api/api";
import ImageUploader, { formatSpec, getCreativeSpec } from "./ImageUploader";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "mr", label: "Marathi" },
  { code: "hi", label: "Hindi" },
];
const STEPS = [
  "Basic Information",
  "Creative",
  "Placement & Action",
  "Targeting",
  "Schedule & Delivery",
  "Preview & Publish",
];
const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;
const DEFAULT_MAP_CENTER = { lat: 21.1458, lng: 79.0882 }; // Nagpur — sensible default center

// Mirrors model/Advertisement.js's enums exactly. Used only if /meta can't
// be reached (expired session, network hiccup, server restarting) — the
// Service/Placement dropdowns should never be silently empty just because
// one lookup call failed; a stale-but-correct list beats a blank dropdown.
const FALLBACK_META = {
  services: [
    "ride",
    "events",
    "movies",
    "food",
    "hotels",
    "bus",
    "train",
    "wallet",
    "shopping",
    "recharge",
    "other",
  ],
  placements: [
    "HOME_HERO",
    "HOME_TOP_PROMO",
    "HOME_OFFER",
    "HOME_OFFER_FOR_YOU",
    "HOME_SERVICE_PROMO",
    "HOME_BETWEEN_SERVICES",
    "RIDE_PROMOTION",
    "EVENT_PROMOTION",
    "MOVIE_PROMOTION",
  ],
  languages: ["en", "mr", "hi"],
  actionTypes: [
    "SERVICE",
    "RIDE_BOOKING",
    "EVENT_LIST",
    "EVENT_DETAILS",
    "MOVIE_LIST",
    "OFFER_DETAILS",
    "IN_APP_SCREEN",
    "DEEP_LINK",
    "EXTERNAL_URL",
  ],
  pricingModels: ["NONE", "CPM", "CPC", "FLAT"],
  customerSegments: ["ALL", "GUEST", "LOGGED_IN", "NEW", "EXISTING"],
  measurableSegments: ["ALL", "GUEST", "LOGGED_IN", "NEW", "EXISTING"],
  newCustomerDays: 30,
};

// What happens when the customer taps the ad. `needs` says which extra
// input the action takes — mirrored by utils/adUrlSafety.js validateAction.
const ACTION_LABELS = {
  SERVICE: { label: "Open the campaign's service", needs: null },
  RIDE_BOOKING: { label: "Start a ride booking", needs: null },
  EVENT_LIST: { label: "Open the events list", needs: null },
  EVENT_DETAILS: { label: "Open a specific event", needs: "event" },
  MOVIE_LIST: { label: "Open the movies list", needs: null },
  OFFER_DETAILS: { label: "Show offer details", needs: null },
  IN_APP_SCREEN: { label: "Open an app screen", needs: "screen" },
  DEEP_LINK: { label: "Custom deep link", needs: "deeplink" },
  EXTERNAL_URL: {
    label: "Open a website (third-party / partner)",
    needs: "url",
  },
};

// App screens an IN_APP_SCREEN action may open — must exist in
// customer_app/src/modules/ads/adActions.ts's SCREEN_ALLOWLIST.
const APP_SCREENS = [
  "RideEntry",
  "OutstationEntry",
  "RentalEntry",
  "Notifications",
  "HelpSupport",
  "Faq",
  "ContactUs",
  "Profile",
];

const SEGMENT_LABELS = {
  ALL: "Everyone",
  GUEST: "Guests (not logged in)",
  LOGGED_IN: "Logged-in customers",
  NEW: "New customers",
  EXISTING: "Existing customers",
  FIRST_RIDE: "First ride (needs ride data)",
  FREQUENT: "Frequent riders (needs ride data)",
  INACTIVE: "Inactive customers (needs ride data)",
  HIGH_VALUE: "High-value customers (needs ride data)",
};

const PRICING_LABELS = {
  NONE: "No billing (in-house promotion)",
  CPM: "CPM — per 1,000 impressions",
  CPC: "CPC — per click",
  FLAT: "Flat fee",
};

const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const numberOrNull = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);

// Friendly labels for the Placement step — the raw enum names (HOME_OFFER,
// HOME_OFFER_FOR_YOU, ...) all look identical at a glance once
// underscore-replaced ("HOME OFFER" vs "HOME OFFER FOR YOU"), which made it
// hard to tell them apart. This also documents which of the 8 placements
// actually render somewhere in the customer app today vs. reserved for a
// future screen — see customer_app's HomeScreen.tsx / home.controller.js.
const PLACEMENT_LABELS = {
  HOME_HERO: "Home Hero (top swipeable slider)",
  HOME_TOP_PROMO: "Home Top Promo (banner below the hero)",
  HOME_OFFER: "Offers for you (carousel)",
  HOME_OFFER_FOR_YOU: "Recommended for you (carousel)",
  HOME_SERVICE_PROMO: "Home Service Promo (banner after Top Services)",
  HOME_BETWEEN_SERVICES: "Home Between Services (banner after Service Promo)",
  RIDE_PROMOTION: "Ride Promotion (banner on the ride booking screen)",
  EVENT_PROMOTION: "Event Promotion (reserved — Events module not live yet)",
  MOVIE_PROMOTION: "Movie Promotion (reserved — Movies module not live yet)",
};

const emptyLangContent = () => ({
  title: "",
  subtitle: "",
  description: "",
  ctaText: "",
});
const emptyCreative = () => ({
  objectKey: null,
  imageUrl: null,
  thumbnailUrl: null,
  width: null,
  height: null,
  fileSize: null,
  mimeType: null,
});

const buildInitialForm = () => ({
  _id: null,
  campaignId: null,
  name: "",
  description: "",
  serviceId: "",
  subcategoryIds: [],
  placement: "",
  priority: 10,
  source: {
    type: "IN_APP",
    advertiser: {
      name: "",
      website: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
    },
    showSponsoredLabel: false,
  },
  content: {
    en: emptyLangContent(),
    mr: emptyLangContent(),
    hi: emptyLangContent(),
  },
  creative: { en: emptyCreative(), mr: emptyCreative(), hi: emptyCreative() },
  targeting: {
    mode: "ALL",
    matchMode: "ANY",
    stateIds: [],
    cityIds: [],
    districts: [],
    areas: [],
    pincodes: [],
    radius: { enabled: false, label: "", center: undefined, radiusKm: 5 },
    languages: [],
    customerSegment: "ALL",
  },
  schedule: { startAt: "", endAt: "" },
  action: {
    type: "SERVICE",
    value: "ride",
    openIn: "EXTERNAL_BROWSER",
    appendUtm: true,
  },
  delivery: { weight: 1, frequencyCapPerDay: "", isFallback: false },
  budget: {
    pricingModel: "NONE",
    rate: 0,
    totalBudget: "",
    maxImpressions: "",
    maxClicks: "",
  },
  status: "DRAFT",
});

/** Normalizes a campaign fetched from the list/detail API (populated stateIds/cityIds are {_id,name} objects) back into the flat id arrays this form's Selects need. */
const normalizeIncoming = (campaign) => ({
  ...buildInitialForm(),
  ...campaign,
  subcategoryIds: (campaign.subcategoryIds || []).map((s) =>
    typeof s === "string" ? s : s._id,
  ),
  source: {
    ...buildInitialForm().source,
    ...campaign.source,
    advertiser: {
      ...buildInitialForm().source.advertiser,
      ...campaign.source?.advertiser,
    },
  },
  action:
    campaign.source?.type === "THIRD_PARTY" &&
    campaign.action?.type !== "EXTERNAL_URL"
      ? { ...buildInitialForm().action, type: "EXTERNAL_URL", value: "" }
      : { ...buildInitialForm().action, ...campaign.action },
  delivery: {
    ...buildInitialForm().delivery,
    ...campaign.delivery,
    frequencyCapPerDay: campaign.delivery?.frequencyCapPerDay ?? "",
  },
  budget: {
    ...buildInitialForm().budget,
    ...campaign.budget,
    totalBudget: campaign.budget?.totalBudget ?? "",
    maxImpressions: campaign.budget?.maxImpressions ?? "",
    maxClicks: campaign.budget?.maxClicks ?? "",
  },
  content: { ...buildInitialForm().content, ...campaign.content },
  creative: { ...buildInitialForm().creative, ...campaign.creative },
  targeting: {
    ...buildInitialForm().targeting,
    ...campaign.targeting,
    stateIds: (campaign.targeting?.stateIds || []).map((s) =>
      typeof s === "string" ? s : s._id,
    ),
    cityIds: (campaign.targeting?.cityIds || []).map((c) =>
      typeof c === "string" ? c : c._id,
    ),
  },
  schedule: {
    startAt: campaign.schedule?.startAt
      ? new Date(campaign.schedule.startAt).toISOString().slice(0, 16)
      : "",
    endAt: campaign.schedule?.endAt
      ? new Date(campaign.schedule.endAt).toISOString().slice(0, 16)
      : "",
  },
});

const AdvertisementForm = () => {
  const { id } = useParams();
  const { state: navState } = useLocation();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(buildInitialForm());
  const [meta, setMeta] = useState(FALLBACK_META);
  const [activeLang, setActiveLang] = useState("en");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [areaInput, setAreaInput] = useState("");
  const [districtInput, setDistrictInput] = useState("");
  const [pincodeInput, setPincodeInput] = useState("");
  const [events, setEvents] = useState([]);
  const [mapApiKey, setMapApiKey] = useState("");
  const { isLoaded: mapLoaded } = useJsApiLoader({
    googleMapsApiKey: mapApiKey,
    libraries: ["places"],
  });

  const [testContext, setTestContext] = useState({
    stateId: "",
    cityId: "",
    district: "",
    area: "",
    pincode: "",
    language: "en",
    customerType: "GUEST",
  });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    getAdvertisementMetaApi()
      .then((res) => {
        const info = res?.data?.info;
        if (info && Object.keys(info).length) setMeta(info);
      })
      .catch((error) => {
        toast.error(
          error?.response?.status === 402 || error?.response?.status === 401
            ? "Your session has expired — please log in again to load Service/Placement options."
            : error?.response?.data?.message ||
                "Couldn't reach the server for Service/Placement options — showing the standard list instead.",
        );
      });
    getAllStateApi()
      .then((res) => setStates(res?.data?.info || []))
      .catch((error) =>
        toast.error(
          error?.response?.data?.message ||
            "Unable to load states for location targeting.",
        ),
      );
    getAppSettingsApi()
      .then((res) => setMapApiKey(res?.data?.info?.map_api_key || ""))
      .catch(() => {}); // non-critical — only powers the optional radius-targeting map
    getAllEventApi()
      .then((res) => setEvents(res?.data?.info || res?.data?.data || []))
      .catch(() => {}); // non-critical — only powers the "Open a specific event" picker
  }, []);

  // Customer App Services drive which services are live; FALLBACK_META only
  // has plain ids, so synthesize options from it when /meta is unreachable.
  const serviceOptions = useMemo(
    () =>
      meta.serviceOptions ||
      (meta.services || []).map((id) => ({
        id,
        label: id.charAt(0).toUpperCase() + id.slice(1),
        status: "UNKNOWN",
        active: true,
        subcategories: [],
      })),
    [meta],
  );
  const selectedService = serviceOptions.find((s) => s.id === form.serviceId);
  const placementInfo = (id) =>
    (meta.placementCatalog || []).find((p) => p.id === id);
  const isThirdParty = form.source.type === "THIRD_PARTY";
  const actionNeeds = ACTION_LABELS[form.action.type]?.needs;

  // Destination URL + open mode + UTM. Third-party ads show this on step 1
  // (their only action); in-app promotions show it on step 3 when "Open a
  // website" is chosen.
  const externalUrlFields = (
    <>
      <TextField
        label="Destination URL"
        placeholder="https://partner.com/landing-page"
        value={form.action.value}
        error={Boolean(form.action.value) && !isHttpsUrl(form.action.value)}
        helperText="Must be https://. Validated again on the server and every time the ad is served."
        onChange={(e) =>
          updateNested("action", { value: e.target.value.trim() })
        }
      />
      <FormControl>
        <InputLabel>Open in</InputLabel>
        <Select
          label="Open in"
          value={form.action.openIn}
          onChange={(e) => updateNested("action", { openIn: e.target.value })}
        >
          <MenuItem value="IN_APP_BROWSER">
            In-app browser (customer stays in BharatOnline)
          </MenuItem>
          <MenuItem value="EXTERNAL_BROWSER">Phone&apos;s browser</MenuItem>
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={form.action.appendUtm !== false}
            onChange={(e) =>
              updateNested("action", {
                appendUtm: e.target.checked,
              })
            }
          />
        }
        label="Add UTM attribution (utm_source=bharatonline, utm_campaign=<campaign code>)"
      />
    </>
  );

  /** Client-side mirror of the server's action validation, so mistakes surface on the step itself. */
  const actionError = () => {
    const value = String(form.action.value || "").trim();
    if (actionNeeds === "url" && !isHttpsUrl(value))
      return "Enter a full https:// destination URL.";
    if (actionNeeds === "deeplink" && !/^bharatonline:\/\/.+/.test(value))
      return "Deep link must look like bharatonline://<screen>.";
    if (actionNeeds === "event" && !value) return "Pick the event to open.";
    if (actionNeeds === "screen" && !value) return "Pick the screen to open.";
    return null;
  };

  useEffect(() => {
    if (!isEdit) return;
    if (navState && navState._id) {
      setForm(normalizeIncoming(navState));
      setLoading(false);
      return;
    }
    // Direct URL / refresh without router state — fetch it ourselves.
    getAdvertisementByIdApi(id)
      .then((res) => setForm(normalizeIncoming(res?.data?.info)))
      .catch(() => toast.error("Unable to load this campaign."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const stateIds = form.targeting.stateIds;
    if (!stateIds.length) {
      setCities([]);
      return;
    }
    Promise.all(
      stateIds.map((stateid) =>
        getCityByStateIdApi({ stateid }).then((res) => res?.data?.info || []),
      ),
    )
      .then((results) => setCities(results.flat()))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.targeting.stateIds]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateNested = (key, patch) =>
    setForm((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  const updateAdvertiser = (patch) =>
    setForm((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        advertiser: { ...prev.source.advertiser, ...patch },
      },
    }));
  const updateTargeting = (patch) =>
    setForm((prev) => ({
      ...prev,
      targeting: { ...prev.targeting, ...patch },
    }));
  const updateContentField = (lang, field, value) =>
    setForm((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [lang]: { ...prev.content[lang], [field]: value },
      },
    }));

  const buildPayload = () => ({
    id: form._id || undefined,
    name: form.name,
    description: form.description,
    serviceId: form.serviceId,
    subcategoryIds: form.subcategoryIds,
    placement: form.placement || meta.placements?.[0] || "HOME_HERO",
    priority: form.priority,
    source: form.source,
    content: form.content,
    creative: form.creative,
    targeting: form.targeting,
    delivery: {
      weight: Number(form.delivery.weight) || 1,
      frequencyCapPerDay: numberOrNull(form.delivery.frequencyCapPerDay),
      isFallback: Boolean(form.delivery.isFallback),
    },
    budget: {
      pricingModel: form.budget.pricingModel,
      rate: Number(form.budget.rate) || 0,
      totalBudget: numberOrNull(form.budget.totalBudget),
      maxImpressions: numberOrNull(form.budget.maxImpressions),
      maxClicks: numberOrNull(form.budget.maxClicks),
    },
    schedule: {
      startAt: form.schedule.startAt
        ? new Date(form.schedule.startAt).toISOString()
        : undefined,
      endAt: form.schedule.endAt
        ? new Date(form.schedule.endAt).toISOString()
        : undefined,
    },
    action: form.action,
  });

  /** Create-if-new / update-if-existing — called whenever we need a real campaignId (before uploading a creative) or when leaving a step, so progress is never silently lost. */
  const persist = async () => {
    setSaving(true);
    try {
      if (!form._id) {
        if (!form.name || !form.serviceId) {
          toast.error(
            "Campaign name and service are required before continuing.",
          );
          return false;
        }
        const response = await addAdvertisementApi(buildPayload());
        const created = response?.data?.info;
        setForm((prev) => ({
          ...prev,
          _id: created._id,
          campaignId: created.campaignId,
        }));
      } else {
        await updateAdvertisementApi(buildPayload());
      }
      return true;
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save campaign.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 0 && (!form.name || !form.serviceId)) {
      toast.error("Campaign name and service are required.");
      return;
    }
    if (activeStep === 0 && isThirdParty && !form.source.advertiser.name) {
      toast.error("Advertiser name is required for a third-party campaign.");
      return;
    }
    if (activeStep === 0 && isThirdParty && actionError()) {
      toast.error(actionError());
      return;
    }
    if (activeStep === 2 && actionError()) {
      toast.error(actionError());
      return;
    }
    const ok = await persist();
    if (ok) setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const handleSaveDraft = async () => {
    const ok = await persist();
    if (ok) {
      toast.success("Draft saved.");
      navigate("/marketing/advertisements");
    }
  };

  const handlePublish = async () => {
    const ok = await persist();
    if (!ok) return;
    try {
      const response = await publishAdvertisementApi(form._id);
      const warnings = response?.data?.info?.warnings || [];
      toast.success("Campaign published.");
      // e.g. service not ACTIVE yet — published, but won't deliver until it is.
      warnings.forEach((w) => toast.warning(w, { autoClose: 8000 }));
      navigate("/marketing/advertisements", {
        state: { publishWarnings: warnings },
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to publish campaign.",
      );
    }
  };

  const addArea = () => {
    const value = areaInput.trim();
    if (!value) return;
    if (!form.targeting.areas.includes(value)) {
      updateTargeting({ areas: [...form.targeting.areas, value] });
    }
    setAreaInput("");
  };
  const removeArea = (value) =>
    updateTargeting({ areas: form.targeting.areas.filter((a) => a !== value) });

  const addDistrict = () => {
    const value = districtInput.trim();
    if (!value) return;
    if (
      !form.targeting.districts.some(
        (d) => d.toLowerCase() === value.toLowerCase(),
      )
    ) {
      updateTargeting({ districts: [...form.targeting.districts, value] });
    }
    setDistrictInput("");
  };
  const removeDistrict = (value) =>
    updateTargeting({
      districts: form.targeting.districts.filter((d) => d !== value),
    });

  const addPincode = () => {
    const value = pincodeInput.trim();
    if (!value) return;
    if (!PINCODE_PATTERN.test(value)) {
      toast.error("Enter a valid 6-digit Indian pincode.");
      return;
    }
    if (!form.targeting.pincodes.includes(value)) {
      updateTargeting({ pincodes: [...form.targeting.pincodes, value] });
    }
    setPincodeInput("");
  };
  const removePincode = (value) =>
    updateTargeting({
      pincodes: form.targeting.pincodes.filter((p) => p !== value),
    });

  const locationSummary = useMemo(() => {
    const t = form.targeting;
    if (t.mode === "ALL") return "All Locations";
    if (t.mode === "RADIUS")
      return t.radius.center
        ? `${t.radius.radiusKm} km around ${t.radius.label || "selected point"}`
        : "No point selected yet";
    const parts = [];
    const stateNames = states
      .filter((s) => t.stateIds.includes(s._id))
      .map((s) => s.name);
    const cityNames = cities
      .filter((c) => t.cityIds.includes(c._id || c.id))
      .map((c) => c.name);
    if (stateNames.length) parts.push(`India → ${stateNames.join(", ")}`);
    if (cityNames.length) parts.push(`→ ${cityNames.join(", ")}`);
    if (t.districts.length) parts.push(`Districts: ${t.districts.join(", ")}`);
    if (t.areas.length) parts.push(`Areas: ${t.areas.join(", ")}`);
    if (t.pincodes.length) parts.push(`Pincodes: ${t.pincodes.join(", ")}`);
    return parts.length
      ? parts.join("  ")
      : "No facets selected yet (unrestricted within Structured mode)";
  }, [form.targeting, states, cities]);

  const runTargetingTest = async () => {
    if (!form._id) {
      toast.error("Save the campaign first.");
      return;
    }
    setTesting(true);
    try {
      const response = await testAdvertisementTargetingApi(form._id, {
        serviceId: form.serviceId,
        placement: form.placement,
        ...testContext,
      });
      setTestResult(response?.data?.info);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Unable to run targeting test.",
      );
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading campaign…</Typography>
      </Box>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>{isEdit ? "Edit Campaign" : "New Campaign"}</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>Marketing</li>
          <li>
            <Link to="/marketing/advertisements">Advertisements</Link>
          </li>
          <li>{isEdit ? "Edit" : "New"}</li>
        </ul>
      </div>

      <Card>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* STEP 0 — Basic Information */}
          {activeStep === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxWidth: 560,
              }}
            >
              <TextField
                label="Campaign Name"
                required
                value={form.name}
                onChange={(e) => updateForm({ name: e.target.value })}
              />
              {form.campaignId ? (
                <TextField
                  label="Campaign Code"
                  value={form.campaignId}
                  disabled
                />
              ) : null}
              <TextField
                label="Description"
                multiline
                minRows={2}
                value={form.description}
                onChange={(e) => updateForm({ description: e.target.value })}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Ad Source
                </Typography>
                <RadioGroup
                  row
                  value={form.source.type}
                  onChange={(e) => {
                    const thirdParty = e.target.value === "THIRD_PARTY";
                    setForm((prev) => ({
                      ...prev,
                      source: { ...prev.source, type: e.target.value },
                      // Third-party = no category, always opens the partner's website.
                      serviceId: thirdParty ? "other" : "",
                      subcategoryIds: [],
                      action: thirdParty
                        ? { ...prev.action, type: "EXTERNAL_URL", value: "" }
                        : { ...prev.action, type: "SERVICE", value: "" },
                    }));
                  }}
                >
                  <FormControlLabel
                    value="IN_APP"
                    control={<Radio />}
                    label="In-app promotion (BharatOnline)"
                  />
                  <FormControlLabel
                    value="THIRD_PARTY"
                    control={<Radio />}
                    label="Third-party advertiser"
                  />
                </RadioGroup>
              </Box>
              {isThirdParty ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                    p: 2,
                    border: "1px solid #eee",
                    borderRadius: 2,
                  }}
                >
                  <TextField
                    label="Advertiser Name"
                    required
                    value={form.source.advertiser.name}
                    onChange={(e) => updateAdvertiser({ name: e.target.value })}
                  />
                  <TextField
                    label="Advertiser Website"
                    placeholder="https://partner.com"
                    value={form.source.advertiser.website}
                    onChange={(e) =>
                      updateAdvertiser({ website: e.target.value })
                    }
                  />
                  <TextField
                    label="Contact Person"
                    value={form.source.advertiser.contactName}
                    onChange={(e) =>
                      updateAdvertiser({ contactName: e.target.value })
                    }
                  />
                  <TextField
                    label="Contact Email"
                    value={form.source.advertiser.contactEmail}
                    onChange={(e) =>
                      updateAdvertiser({ contactEmail: e.target.value })
                    }
                  />
                  <TextField
                    label="Contact Phone"
                    value={form.source.advertiser.contactPhone}
                    onChange={(e) =>
                      updateAdvertiser({ contactPhone: e.target.value })
                    }
                  />
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    {externalUrlFields}
                  </Box>
                  <Alert severity="info" sx={{ gridColumn: "1 / -1", py: 0 }}>
                    Third-party ads are not tied to any category — they can run
                    in any slot and tapping one always opens the URL above. They
                    always show a &quot;Sponsored&quot; label; advertiser
                    contact details are never sent to the app.
                  </Alert>
                </Box>
              ) : (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(form.source.showSponsoredLabel)}
                      onChange={(e) =>
                        updateNested("source", {
                          showSponsoredLabel: e.target.checked,
                        })
                      }
                    />
                  }
                  label='Show a "Sponsored" label on this promotion'
                />
              )}
              {!isThirdParty ? (
                <>
                  <FormControl required>
                    <InputLabel>Service / Category</InputLabel>
                    <Select
                      label="Service / Category"
                      value={form.serviceId}
                      onChange={(e) =>
                        updateForm({
                          serviceId: e.target.value,
                          subcategoryIds: [],
                          // The default "open the service" action follows the service.
                          action:
                            form.action.type === "SERVICE"
                              ? { ...form.action, value: e.target.value }
                              : form.action,
                        })
                      }
                    >
                      <ListSubheader>Active in the customer app</ListSubheader>
                      {serviceOptions
                        .filter((s) => s.active && s.id !== "other")
                        .map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.label}
                          </MenuItem>
                        ))}
                      <ListSubheader>
                        Not active — campaigns won&apos;t be shown until the
                        service is ACTIVE
                      </ListSubheader>
                      {serviceOptions
                        .filter((s) => !s.active && s.id !== "other")
                        .map((s) => (
                          <MenuItem
                            key={s.id}
                            value={s.id}
                            sx={{ opacity: 0.7 }}
                          >
                            {s.label} (
                            {s.status.replace(/_/g, " ").toLowerCase()})
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  {selectedService && !selectedService.active ? (
                    <Alert severity="warning" sx={{ py: 0 }}>
                      {selectedService.label} is not ACTIVE in App Configuration
                      → Customer App Services. You can prepare and publish this
                      campaign now; customers will see it only once the service
                      is activated.
                    </Alert>
                  ) : null}
                  {selectedService?.subcategories?.length ? (
                    <FormControl>
                      <InputLabel>Subcategories (optional)</InputLabel>
                      <Select
                        multiple
                        label="Subcategories (optional)"
                        value={form.subcategoryIds}
                        onChange={(e) =>
                          updateForm({ subcategoryIds: e.target.value })
                        }
                        renderValue={(selected) =>
                          selectedService.subcategories
                            .filter((s) => selected.includes(s.id))
                            .map((s) => s.name)
                            .join(", ")
                        }
                      >
                        {selectedService.subcategories.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                            {s.active ? "" : " (inactive)"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                </>
              ) : null}
              <TextField
                label="Priority"
                type="number"
                helperText="Higher priority wins when campaigns compete for the same placement (e.g. city campaign 100 > state 50 > national 10)."
                value={form.priority}
                onChange={(e) =>
                  updateForm({ priority: Number(e.target.value) })
                }
              />
              {form.status ? (
                <Chip
                  label={`Status: ${form.status}`}
                  sx={{ alignSelf: "flex-start" }}
                />
              ) : null}
            </Box>
          ) : null}

          {/* STEP 1 — Creative Content */}
          {activeStep === 1 ? (
            <Box>
              <Tabs
                value={activeLang}
                onChange={(_, v) => setActiveLang(v)}
                sx={{ mb: 2 }}
              >
                {LANGUAGES.map((lang) => (
                  <Tab
                    key={lang.code}
                    value={lang.code}
                    label={
                      form.creative[lang.code]?.imageUrl
                        ? `${lang.label} ✓`
                        : lang.label
                    }
                  />
                ))}
              </Tabs>
              {LANGUAGES.map((lang) =>
                lang.code === activeLang ? (
                  <Box
                    key={lang.code}
                    sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}
                  >
                    <Box
                      sx={{
                        flex: "1 1 320px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <Alert severity="info" sx={{ py: 0 }}>
                        Text is optional — leave all fields empty for an
                        image-only banner (text designed into the image). The
                        app then shows the image with no overlay.
                      </Alert>
                      <TextField
                        label="Title (optional)"
                        value={form.content[lang.code].title}
                        onChange={(e) =>
                          updateContentField(lang.code, "title", e.target.value)
                        }
                      />
                      <TextField
                        label="Subtitle (optional)"
                        value={form.content[lang.code].subtitle}
                        onChange={(e) =>
                          updateContentField(
                            lang.code,
                            "subtitle",
                            e.target.value,
                          )
                        }
                      />
                      <TextField
                        label="Description (optional)"
                        multiline
                        minRows={2}
                        value={form.content[lang.code].description}
                        onChange={(e) =>
                          updateContentField(
                            lang.code,
                            "description",
                            e.target.value,
                          )
                        }
                      />
                      <TextField
                        label="CTA Text (optional)"
                        value={form.content[lang.code].ctaText}
                        onChange={(e) =>
                          updateContentField(
                            lang.code,
                            "ctaText",
                            e.target.value,
                          )
                        }
                      />
                    </Box>
                    <Box sx={{ flex: "1 1 320px" }}>
                      <ImageUploader
                        campaignId={form.campaignId}
                        language={lang.code}
                        placement={form.placement}
                        value={form.creative[lang.code]}
                        label={`${lang.label} banner image`}
                        onUploaded={(info) =>
                          setForm((prev) => ({
                            ...prev,
                            creative: { ...prev.creative, [lang.code]: info },
                          }))
                        }
                        onRemove={() =>
                          setForm((prev) => ({
                            ...prev,
                            creative: {
                              ...prev.creative,
                              [lang.code]: emptyCreative(),
                            },
                          }))
                        }
                      />
                    </Box>
                  </Box>
                ) : null,
              )}
              {!form.campaignId ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Basic info is saved automatically as a draft so images can be
                  uploaded — go back to Step 1 if you need to change the name or
                  service.
                </Alert>
              ) : null}
            </Box>
          ) : null}

          {/* STEP 2 — Placement */}
          {activeStep === 2 ? (
            <Box sx={{ maxWidth: 560 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Where on the customer app should this campaign appear? React
                Native owns the exact layout — this only controls which content
                slot is used.
              </Typography>
              <RadioGroup
                value={form.placement}
                onChange={(e) => updateForm({ placement: e.target.value })}
              >
                {(meta.placements || []).map((p) => {
                  const info = placementInfo(p);
                  return (
                    <FormControlLabel
                      key={p}
                      value={p}
                      control={<Radio />}
                      label={`${PLACEMENT_LABELS[p] || p.replace(/_/g, " ")}${info && !info.live ? " — screen not live yet" : ""}${getCreativeSpec(p) ? ` — image ${formatSpec(getCreativeSpec(p))}` : ""}`}
                    />
                  );
                })}
              </RadioGroup>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Click Action
              </Typography>
              {isThirdParty ? (
                <Alert severity="info">
                  Tapping this ad opens the advertiser&apos;s website:{" "}
                  <strong>
                    {form.action.value || "not set — go back to step 1"}
                  </strong>
                </Alert>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl>
                    <InputLabel>When tapped</InputLabel>
                    <Select
                      label="When tapped"
                      value={form.action.type}
                      onChange={(e) => {
                        const type = e.target.value;
                        updateNested("action", {
                          type,
                          value: type === "SERVICE" ? form.serviceId : "",
                        });
                      }}
                    >
                      {(meta.actionTypes || []).map((a) => (
                        <MenuItem key={a} value={a}>
                          {ACTION_LABELS[a]?.label || a.replace(/_/g, " ")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {actionNeeds === "url" ? externalUrlFields : null}

                  {actionNeeds === "deeplink" ? (
                    <TextField
                      label="Deep link"
                      placeholder="bharatonline://ride/outstation"
                      value={form.action.value}
                      helperText="App paths: ride, ride/outstation, ride/rental, notifications, orders, profile. Anything else opens a Coming Soon page."
                      onChange={(e) =>
                        updateNested("action", { value: e.target.value.trim() })
                      }
                    />
                  ) : null}

                  {actionNeeds === "screen" ? (
                    <FormControl>
                      <InputLabel>App screen</InputLabel>
                      <Select
                        label="App screen"
                        value={form.action.value}
                        onChange={(e) =>
                          updateNested("action", { value: e.target.value })
                        }
                      >
                        {APP_SCREENS.map((s) => (
                          <MenuItem key={s} value={s}>
                            {s}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  {actionNeeds === "event" ? (
                    <FormControl>
                      <InputLabel>Event</InputLabel>
                      <Select
                        label="Event"
                        value={form.action.value}
                        onChange={(e) =>
                          updateNested("action", { value: e.target.value })
                        }
                      >
                        {events.map((ev) => (
                          <MenuItem key={ev._id} value={ev._id}>
                            {ev.title || ev.event_name || ev.name || ev._id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                </Box>
              )}
            </Box>
          ) : null}

          {/* STEP 3 — Targeting */}
          {activeStep === 3 ? (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Targeting Mode
              </Typography>
              <RadioGroup
                row
                value={form.targeting.mode}
                onChange={(e) => updateTargeting({ mode: e.target.value })}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="ALL"
                  control={<Radio />}
                  label="All Locations"
                />
                <FormControlLabel
                  value="STRUCTURED"
                  control={<Radio />}
                  label="State / City / Area / Pincode"
                />
                <FormControlLabel
                  value="RADIUS"
                  control={<Radio />}
                  label="Radius"
                />
              </RadioGroup>

              {form.targeting.mode === "STRUCTURED" ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    maxWidth: 640,
                  }}
                >
                  <FormControl>
                    <InputLabel>Match</InputLabel>
                    <Select
                      label="Match"
                      value={form.targeting.matchMode}
                      onChange={(e) =>
                        updateTargeting({ matchMode: e.target.value })
                      }
                      sx={{ maxWidth: 260 }}
                    >
                      <MenuItem value="ANY">Match ANY selected facet</MenuItem>
                      <MenuItem value="ALL">Match ALL selected facets</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <InputLabel>State</InputLabel>
                    <Select
                      multiple
                      label="State"
                      value={form.targeting.stateIds}
                      onChange={(e) =>
                        updateTargeting({
                          stateIds: e.target.value,
                          cityIds: [],
                        })
                      }
                      renderValue={(selected) =>
                        states
                          .filter((s) => selected.includes(s._id))
                          .map((s) => s.name)
                          .join(", ")
                      }
                    >
                      {states.map((s) => (
                        <MenuItem key={s._id} value={s._id}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl disabled={!cities.length}>
                    <InputLabel>City</InputLabel>
                    <Select
                      multiple
                      label="City"
                      value={form.targeting.cityIds}
                      onChange={(e) =>
                        updateTargeting({ cityIds: e.target.value })
                      }
                      renderValue={(selected) =>
                        cities
                          .filter((c) => selected.includes(c._id || c.id))
                          .map((c) => c.name)
                          .join(", ")
                      }
                    >
                      {cities.map((c) => (
                        <MenuItem key={c._id || c.id} value={c._id || c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Districts (matched against the customer&apos;s detected
                      district)
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}
                    >
                      {form.targeting.districts.map((d) => (
                        <Chip
                          key={d}
                          label={d}
                          onDelete={() => removeDistrict(d)}
                          size="small"
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g. Nagpur"
                        value={districtInput}
                        onChange={(e) => setDistrictInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), addDistrict())
                        }
                      />
                      <Button onClick={addDistrict}>+ Add District</Button>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Areas
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}
                    >
                      {form.targeting.areas.map((a) => (
                        <Chip
                          key={a}
                          label={a}
                          onDelete={() => removeArea(a)}
                          size="small"
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g. Dharampeth"
                        value={areaInput}
                        onChange={(e) => setAreaInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" && (e.preventDefault(), addArea())
                        }
                      />
                      <Button onClick={addArea}>+ Add Area</Button>
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Target Pincodes
                    </Typography>
                    <Box
                      sx={{ display: "flex", gap: 1, flexWrap: "wrap", my: 1 }}
                    >
                      {form.targeting.pincodes.map((p) => (
                        <Chip
                          key={p}
                          label={p}
                          onDelete={() => removePincode(p)}
                          size="small"
                        />
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <TextField
                        size="small"
                        placeholder="e.g. 440010"
                        value={pincodeInput}
                        onChange={(e) => setPincodeInput(e.target.value)}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          (e.preventDefault(), addPincode())
                        }
                      />
                      <Button onClick={addPincode}>+ Add Pincode</Button>
                    </Box>
                  </Box>
                </Box>
              ) : null}

              {form.targeting.mode === "RADIUS" ? (
                <Box sx={{ maxWidth: 640 }}>
                  <TextField
                    label="Location Label"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={form.targeting.radius.label}
                    onChange={(e) =>
                      updateTargeting({
                        radius: {
                          ...form.targeting.radius,
                          label: e.target.value,
                        },
                      })
                    }
                    placeholder="e.g. Bharat Online Office"
                  />
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      label="Latitude"
                      type="number"
                      value={
                        form.targeting.radius.center?.coordinates?.[1] ?? ""
                      }
                      onChange={(e) => {
                        const lat = Number(e.target.value);
                        const lng =
                          form.targeting.radius.center?.coordinates?.[0] ??
                          DEFAULT_MAP_CENTER.lng;
                        updateTargeting({
                          radius: {
                            ...form.targeting.radius,
                            enabled: true,
                            center: { type: "Point", coordinates: [lng, lat] },
                          },
                        });
                      }}
                    />
                    <TextField
                      label="Longitude"
                      type="number"
                      value={
                        form.targeting.radius.center?.coordinates?.[0] ?? ""
                      }
                      onChange={(e) => {
                        const lng = Number(e.target.value);
                        const lat =
                          form.targeting.radius.center?.coordinates?.[1] ??
                          DEFAULT_MAP_CENTER.lat;
                        updateTargeting({
                          radius: {
                            ...form.targeting.radius,
                            enabled: true,
                            center: { type: "Point", coordinates: [lng, lat] },
                          },
                        });
                      }}
                    />
                    <TextField
                      label="Radius (KM)"
                      type="number"
                      value={form.targeting.radius.radiusKm}
                      onChange={(e) =>
                        updateTargeting({
                          radius: {
                            ...form.targeting.radius,
                            radiusKm: Number(e.target.value),
                          },
                        })
                      }
                    />
                  </Box>
                  {mapLoaded && mapApiKey ? (
                    <GoogleMap
                      mapContainerStyle={{
                        width: "100%",
                        height: 320,
                        borderRadius: 8,
                      }}
                      center={
                        form.targeting.radius.center
                          ? {
                              lat: form.targeting.radius.center.coordinates[1],
                              lng: form.targeting.radius.center.coordinates[0],
                            }
                          : DEFAULT_MAP_CENTER
                      }
                      zoom={12}
                      onClick={(event) => {
                        const lat = Number(event.latLng.lat());
                        const lng = Number(event.latLng.lng());
                        updateTargeting({
                          radius: {
                            ...form.targeting.radius,
                            enabled: true,
                            center: { type: "Point", coordinates: [lng, lat] },
                          },
                        });
                      }}
                    >
                      {form.targeting.radius.center ? (
                        <>
                          <Marker
                            position={{
                              lat: form.targeting.radius.center.coordinates[1],
                              lng: form.targeting.radius.center.coordinates[0],
                            }}
                            draggable
                            onDragEnd={(event) => {
                              const lat = Number(event.latLng.lat());
                              const lng = Number(event.latLng.lng());
                              updateTargeting({
                                radius: {
                                  ...form.targeting.radius,
                                  center: {
                                    type: "Point",
                                    coordinates: [lng, lat],
                                  },
                                },
                              });
                            }}
                          />
                          <Circle
                            center={{
                              lat: form.targeting.radius.center.coordinates[1],
                              lng: form.targeting.radius.center.coordinates[0],
                            }}
                            radius={
                              (form.targeting.radius.radiusKm || 0) * 1000
                            }
                            options={{ fillOpacity: 0.12, strokeOpacity: 0.6 }}
                          />
                        </>
                      ) : null}
                    </GoogleMap>
                  ) : (
                    <Alert severity="info">
                      Click on the map (once it loads) to drop a pin, or type
                      coordinates directly above.
                    </Alert>
                  )}
                </Box>
              ) : null}

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2">Location Summary</Typography>
              <Typography variant="body2" color="text.secondary">
                {locationSummary}
              </Typography>

              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Audience
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  maxWidth: 420,
                }}
              >
                <FormControl>
                  <InputLabel>Customer segment</InputLabel>
                  <Select
                    label="Customer segment"
                    value={form.targeting.customerSegment || "ALL"}
                    onChange={(e) =>
                      updateTargeting({ customerSegment: e.target.value })
                    }
                  >
                    {(meta.customerSegments || []).map((s) => (
                      <MenuItem
                        key={s}
                        value={s}
                        disabled={!(meta.measurableSegments || []).includes(s)}
                      >
                        {SEGMENT_LABELS[s] || s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {["NEW", "EXISTING"].includes(
                  form.targeting.customerSegment,
                ) ? (
                  <Typography variant="caption" color="text.secondary">
                    New = registered within the last{" "}
                    {meta.newCustomerDays || 30} days. Guests never match
                    New/Existing.
                  </Typography>
                ) : null}
                <FormControl>
                  <InputLabel shrink>Languages</InputLabel>
                  <Select
                    multiple
                    notched
                    label="Languages"
                    value={form.targeting.languages}
                    onChange={(e) =>
                      updateTargeting({ languages: e.target.value })
                    }
                    renderValue={(selected) =>
                      selected.length
                        ? LANGUAGES.filter((l) => selected.includes(l.code))
                            .map((l) => l.label)
                            .join(", ")
                        : "All languages"
                    }
                    displayEmpty
                  >
                    {LANGUAGES.map((l) => (
                      <MenuItem key={l.code} value={l.code}>
                        {l.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          ) : null}

          {/* STEP 4 — Schedule */}
          {activeStep === 4 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxWidth: 420,
              }}
            >
              <TextField
                label="Start"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.schedule.startAt}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    schedule: { ...prev.schedule, startAt: e.target.value },
                  }))
                }
              />
              <TextField
                label="End"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.schedule.endAt}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    schedule: { ...prev.schedule, endAt: e.target.value },
                  }))
                }
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">
                Rotation &amp; Frequency
              </Typography>
              <TextField
                label="Rotation weight"
                type="number"
                inputProps={{ min: 1, max: 100 }}
                helperText="Campaigns with the same priority in the same slot share it in proportion to weight (3 vs 1 ≈ 75% / 25%)."
                value={form.delivery.weight}
                onChange={(e) =>
                  updateNested("delivery", { weight: e.target.value })
                }
              />
              <TextField
                label="Max impressions per customer per day"
                type="number"
                inputProps={{ min: 0, max: 100 }}
                helperText="Leave empty for no limit."
                value={form.delivery.frequencyCapPerDay}
                onChange={(e) =>
                  updateNested("delivery", {
                    frequencyCapPerDay: e.target.value,
                  })
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.delivery.isFallback)}
                    onChange={(e) =>
                      updateNested("delivery", { isFallback: e.target.checked })
                    }
                  />
                }
                label="Fallback campaign — show only when no other campaign is eligible for this slot"
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Budget &amp; Limits</Typography>
              <FormControl>
                <InputLabel>Pricing model</InputLabel>
                <Select
                  label="Pricing model"
                  value={form.budget.pricingModel}
                  onChange={(e) =>
                    updateNested("budget", { pricingModel: e.target.value })
                  }
                >
                  {(meta.pricingModels || []).map((p) => (
                    <MenuItem key={p} value={p}>
                      {PRICING_LABELS[p] || p}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {["CPM", "CPC"].includes(form.budget.pricingModel) ? (
                <TextField
                  label={
                    form.budget.pricingModel === "CPM"
                      ? "Rate (₹ per 1,000 impressions)"
                      : "Rate (₹ per click)"
                  }
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  value={form.budget.rate}
                  onChange={(e) =>
                    updateNested("budget", { rate: e.target.value })
                  }
                />
              ) : null}
              {form.budget.pricingModel !== "NONE" ? (
                <TextField
                  label="Total budget (₹)"
                  type="number"
                  inputProps={{ min: 0 }}
                  helperText={
                    ["CPM", "CPC"].includes(form.budget.pricingModel)
                      ? "Delivery stops automatically when spend reaches this amount."
                      : "Recorded for reporting."
                  }
                  value={form.budget.totalBudget}
                  onChange={(e) =>
                    updateNested("budget", { totalBudget: e.target.value })
                  }
                />
              ) : null}
              <TextField
                label="Stop after impressions"
                type="number"
                inputProps={{ min: 0 }}
                helperText="Leave empty for no limit."
                value={form.budget.maxImpressions}
                onChange={(e) =>
                  updateNested("budget", { maxImpressions: e.target.value })
                }
              />
              <TextField
                label="Stop after clicks"
                type="number"
                inputProps={{ min: 0 }}
                helperText="Leave empty for no limit."
                value={form.budget.maxClicks}
                onChange={(e) =>
                  updateNested("budget", { maxClicks: e.target.value })
                }
              />
            </Box>
          ) : null}

          {/* STEP 5 — Preview & Publish */}
          {activeStep === 5 ? (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                {form.name || "Untitled campaign"}
              </Typography>
              <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3 }}>
                {LANGUAGES.map((lang) =>
                  form.creative[lang.code]?.imageUrl ||
                  form.content[lang.code]?.title ? (
                    <Box key={lang.code} sx={{ width: 260 }}>
                      <Typography variant="caption" color="text.secondary">
                        {lang.label}
                      </Typography>
                      <Box
                        sx={{
                          border: "1px solid #eee",
                          borderRadius: 2,
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {isThirdParty || form.source.showSponsoredLabel ? (
                          <Chip
                            size="small"
                            label="Sponsored"
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              bgcolor: "rgba(0,0,0,0.6)",
                              color: "#fff",
                              height: 20,
                              fontSize: 11,
                            }}
                          />
                        ) : null}
                        {form.creative[lang.code]?.imageUrl ? (
                          <Box
                            component="img"
                            src={form.creative[lang.code].imageUrl}
                            sx={{
                              width: "100%",
                              aspectRatio: `${getCreativeSpec(form.placement)?.ratio || 2}`,
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        ) : (
                          <Box sx={{ height: 130, bgcolor: "#f5f5f5" }} />
                        )}
                        {form.content[lang.code]?.title ||
                        form.content[lang.code]?.subtitle ||
                        form.content[lang.code]?.ctaText ? (
                          <Box sx={{ p: 1.5 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700 }}
                            >
                              {form.content[lang.code]?.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {form.content[lang.code]?.subtitle}
                            </Typography>
                            {form.content[lang.code]?.ctaText ? (
                              <Chip
                                size="small"
                                label={form.content[lang.code].ctaText}
                                sx={{ mt: 1 }}
                              />
                            ) : null}
                          </Box>
                        ) : null}
                      </Box>
                    </Box>
                  ) : null,
                )}
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    SERVICE
                  </Typography>
                  <Typography variant="body2">
                    {isThirdParty
                      ? "No category (third-party)"
                      : form.serviceId}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PLACEMENT
                  </Typography>
                  <Typography variant="body2">{form.placement}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    LOCATION
                  </Typography>
                  <Typography variant="body2">{locationSummary}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    SCHEDULE
                  </Typography>
                  <Typography variant="body2">
                    {form.schedule.startAt
                      ? new Date(form.schedule.startAt).toLocaleString()
                      : "—"}{" "}
                    →{" "}
                    {form.schedule.endAt
                      ? new Date(form.schedule.endAt).toLocaleString()
                      : "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    PRIORITY
                  </Typography>
                  <Typography variant="body2">{form.priority}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    SOURCE
                  </Typography>
                  <Typography variant="body2">
                    {isThirdParty
                      ? `Third-party — ${form.source.advertiser.name || "advertiser not set"}`
                      : "In-app promotion"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    CLICK ACTION
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {ACTION_LABELS[form.action.type]?.label || form.action.type}
                    {actionNeeds && form.action.value
                      ? ` — ${form.action.value}`
                      : ""}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    AUDIENCE
                  </Typography>
                  <Typography variant="body2">
                    {SEGMENT_LABELS[form.targeting.customerSegment || "ALL"]}
                    {form.targeting.languages.length
                      ? ` · ${form.targeting.languages.join(", ").toUpperCase()}`
                      : " · All languages"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    DELIVERY
                  </Typography>
                  <Typography variant="body2">
                    Weight {form.delivery.weight || 1}
                    {form.delivery.frequencyCapPerDay
                      ? ` · max ${form.delivery.frequencyCapPerDay}/customer/day`
                      : ""}
                    {form.delivery.isFallback ? " · fallback" : ""}
                    {form.budget.pricingModel !== "NONE"
                      ? ` · ${form.budget.pricingModel}${form.budget.totalBudget ? ` ₹${form.budget.totalBudget}` : ""}`
                      : ""}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Test Targeting
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Check whether this campaign would be eligible for a hypothetical
                customer, before publishing.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>State</InputLabel>
                  <Select
                    label="State"
                    value={testContext.stateId}
                    onChange={(e) =>
                      setTestContext((p) => ({ ...p, stateId: e.target.value }))
                    }
                  >
                    <MenuItem value="">—</MenuItem>
                    {states.map((s) => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Pincode"
                  value={testContext.pincode}
                  onChange={(e) =>
                    setTestContext((p) => ({ ...p, pincode: e.target.value }))
                  }
                />
                <TextField
                  size="small"
                  label="District"
                  value={testContext.district}
                  onChange={(e) =>
                    setTestContext((p) => ({ ...p, district: e.target.value }))
                  }
                />
                <TextField
                  size="small"
                  label="Area"
                  value={testContext.area}
                  onChange={(e) =>
                    setTestContext((p) => ({ ...p, area: e.target.value }))
                  }
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Customer</InputLabel>
                  <Select
                    label="Customer"
                    value={testContext.customerType}
                    onChange={(e) =>
                      setTestContext((p) => ({
                        ...p,
                        customerType: e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="GUEST">Guest</MenuItem>
                    <MenuItem value="NEW">New customer</MenuItem>
                    <MenuItem value="EXISTING">Existing customer</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                  <InputLabel>Language</InputLabel>
                  <Select
                    label="Language"
                    value={testContext.language}
                    onChange={(e) =>
                      setTestContext((p) => ({
                        ...p,
                        language: e.target.value,
                      }))
                    }
                  >
                    {LANGUAGES.map((l) => (
                      <MenuItem key={l.code} value={l.code}>
                        {l.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  disabled={testing}
                  onClick={runTargetingTest}
                >
                  {testing ? "Testing…" : "Test Campaign"}
                </Button>
              </Box>
              {testResult ? (
                <Alert severity={testResult.eligible ? "success" : "warning"}>
                  {testResult.eligible
                    ? "✓ Campaign eligible"
                    : "✕ Campaign not eligible"}
                  {testResult.reasons?.length ? (
                    <Box component="ul" sx={{ m: "4px 0 0", pl: 2 }}>
                      {testResult.reasons.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </Box>
                  ) : null}
                </Alert>
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="outlined"
                disabled={saving}
                onClick={handleSaveDraft}
              >
                Save Draft
              </Button>
              {activeStep < STEPS.length - 1 ? (
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={handleNext}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="success"
                  disabled={saving}
                  onClick={handlePublish}
                >
                  Publish
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </>
  );
};

export default AdvertisementForm;
